import { useEffect, useState } from "react";
import { http, toastApiError, postDownload } from "../lib/api";
import { safePath } from "../lib/nav";
import { toast } from "../lib/notify";
import { fmtTL, fmtDate } from "../lib/format";
import { AlertTriangle, Clock, Calendar, CheckCircle2, TrendingUp, TrendingDown, FileWarning, ExternalLink, ChevronDown, ChevronRight, FileSpreadsheet, Landmark } from "lucide-react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { FilterTh, FilterTotal, useColFilters } from "../components/ColFilters";

const SUM_TOTAL = [{ key: "total_amount", label: "Tutar" }];
const SUM_ESTIMATED = [{ key: "estimated_total", label: "Tahmini Hakediş" }];
const PP_COLS = [
  { key: "vendor", label: "Firma" },
  { key: "invoice_no", label: "Fatura/Çek No", type: "text", value: (r) => r.invoice_no || "—" },
  { key: "due_date", label: "Ödeme/Vade Tarihi", type: "datemulti", fmt: fmtDate },
  { key: "_group", label: "Kategori / Banka", value: (r) => (r.kind === "check" || r.kind === "loan" ? r.bank : r.category || "—") },
  { key: "description", label: "Açıklama", type: "text", value: (r) => r.description || "—" },
  { key: "total_amount", label: "Tutar", type: "number" },
  { key: "_status", label: "Durum", value: (r) => (r.paid ? "Ödendi" : "Ödenmedi") },
];

const DRAFT_COLS = [
  { key: "vendor", label: "Firma" },
  { key: "invoice_no", label: "Fatura No", type: "text", value: (r) => r.invoice_no || "—" },
  { key: "invoice_date", label: "Fatura Tarihi", type: "datemulti", fmt: fmtDate },
  { key: "due_date", label: "Ödeme Tarihi", type: "datemulti", fmt: fmtDate },
  { key: "project_name", label: "Proje", value: (r) => r.project_name || "—" },
  { key: "_tur", label: "Tür", options: [{ value: "Gelir", label: "Gelir" }, { value: "Gider", label: "Gider" }], value: (r) => (r.direction === "income" ? "Gelir" : "Gider") },
  { key: "total_amount", label: "Tutar", type: "number" },
];

const UNINV_COLS = [
  { key: "project_name", label: "Proje" },
  { key: "category_name", label: "Ana Başlık" },
  { key: "period_label", label: "Dönem" },
  { key: "deadline", label: "Son Tarih", type: "datemulti", fmt: fmtDate },
  { key: "estimated_total", label: "Tahmini Hakediş", type: "number" },
];

const bucketMeta = [
  { key: "overdue", label: "Gecikmiş", icon: AlertTriangle, color: "red" },
  { key: "today", label: "Bugün Ödenecekler", icon: Clock, color: "amber" },
  { key: "paid_today", label: "Bugün Ödenenler", icon: CheckCircle2, color: "emerald" },
  { key: "week", label: "7 Gün İçinde", icon: Calendar, color: "sky" },
  { key: "later", label: "İleri Tarihli", icon: Calendar, color: "slate" },
  { key: "paid", label: "Tamamlandı", icon: CheckCircle2, color: "emerald" },
];

const colorClasses = {
  red: "border-red-200 bg-red-50 text-red-800",
  amber: "border-amber-200 bg-amber-50 text-amber-900",
  sky: "border-sky-200 bg-sky-50 text-sky-900",
  slate: "border-slate-200 bg-slate-50 text-slate-800",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
};

export default function PaymentPlan() {
  const [data, setData] = useState(null);
  const [uninvoiced, setUninvoiced] = useState([]);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const focusId = searchParams.get("kayit") || "";
  const returnTo = safePath(searchParams.get("don")) || "";
  useEffect(() => {
    if (!returnTo) return;
    const onKey = (e) => { if (e.key === "Escape" && !e.target.closest("input,textarea,select")) navigate(returnTo); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [returnTo, navigate]);
  useEffect(() => {
    if (!focusId || !data) return;
    const t = setTimeout(() => document.querySelector(`[data-pp-id="${focusId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 250);
    return () => clearTimeout(t);
  }, [focusId, data]);
  const load = () => {
    http.get("/invoices/payment-plan").then((r) => setData(r.data));
    http.get("/reports/uninvoiced-claims").then((r) => setUninvoiced(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    // eslint-disable-next-line
  }, []);
  if (!data) return <div className="text-sm text-slate-500">Yükleniyor…</div>;

  const exp = data.expense;
  const inc = data.income;

  return (
    <div className="space-y-8" data-testid="page-payment-plan">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Ödeme Planı & Nakit Akış</h1>
        <p className="text-sm text-slate-500 mt-1">
          Ödenecek gider: <span className="font-semibold text-red-700">{fmtTL(exp.unpaid_total)}</span>
          <span className="mx-3 text-slate-300">|</span>
          Tahsil edilecek gelir: <span className="font-semibold text-emerald-700">{fmtTL(inc.unpaid_total)}</span>
          <span className="mx-3 text-slate-300">|</span>
          Net: <span className="font-semibold text-slate-900">{fmtTL(inc.unpaid_total - exp.unpaid_total)}</span>
        </p>
      </div>

      {uninvoiced.length > 0 && (
        <div className="bg-white border border-red-200 rounded-lg overflow-hidden" data-testid="section-uninvoiced">
          <div className="px-5 py-3 border-b border-red-100 bg-red-50/60 flex items-center gap-2 flex-wrap">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <span className="text-sm font-semibold text-red-900">Faturası Kesilmeyen Hakedişler</span>
            <span className="text-[11px] text-slate-500">Veri girişi yapılmış ancak hakedişi oluşmamış dönemler — hakediş, takip eden ayın 10'una kadar oluşturulmalıdır; oluştuğunda listeden düşer.</span>
            <span className="ml-auto text-xs font-mono font-bold text-red-700" data-testid="uninvoiced-total">{fmtTL(uninvoiced.reduce((a, u) => a + (u.estimated_total || 0), 0))}</span>
          </div>
          <UninvoicedTable rows={uninvoiced} />
          <div className="px-5 py-2.5 border-t border-red-100 bg-red-50/40 text-[11px] text-red-800" data-testid="uninvoiced-note">
            Mutabakat sağlanması halinde tahminen toplam <span className="font-bold">{fmtTL(uninvoiced.reduce((a, u) => a + (u.estimated_total || 0), 0))}</span> tutarında hakediş faturası kesilmemiştir.
          </div>
        </div>
      )}

      {data.drafts && data.drafts.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-lg overflow-hidden" data-testid="section-drafts">
          <div className="px-5 py-3 border-b border-amber-100 bg-amber-50/60 flex items-center gap-2">
            <FileWarning className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-semibold">Onay Bekleyen Faturalar</span>
            <span className="text-[11px] text-slate-500">Taslak hakediş faturaları — onaylanana kadar ödeme planına ve bütçeye dahil edilmez.</span>
            <span className="ml-auto text-xs font-mono font-bold" data-testid="drafts-total">{fmtTL(data.drafts.reduce((a, d) => a + (d.total_amount || 0), 0))}</span>
          </div>
          <DraftsTable drafts={data.drafts} />
        </div>
      )}

      {returnTo && (
        <div className="px-4 py-2 rounded-md bg-amber-50 border border-amber-300 text-xs text-amber-900 flex items-center gap-2" data-testid="pp-return-hint" data-return-bar="1">
          İlgili kayıt vurgulandı — durumunu değiştirdikten sonra <b>ESC</b> ile Fiş/Fatura listesine dönebilirsiniz.
          <button type="button" onClick={() => navigate(returnTo)} data-testid="pp-return-btn" className="ml-auto px-2.5 py-1 rounded-md border border-amber-400 bg-white font-semibold hover:bg-amber-100">Geri Dön (ESC)</button>
        </div>
      )}

      <Section title="Gider Faturaları & Verilen Çekler — Ödenecek" note="Sarı satırlar verilen çeklerdir" icon={TrendingDown} part={exp} testidPrefix="expense" onChanged={load} focusId={focusId} />
      <Section title="Gelir Faturaları — Tahsil Edilecek" icon={TrendingUp} part={inc} testidPrefix="income" onChanged={load} focusId={focusId} />
    </div>
  );
}

const BANK_FILES = [
  { key: "ziraat", label: "Ziraat EFT", title: "Ziraat Bankası anlık toplu trans/EFT şablonu (kayıtlı format) — görünen gider faturaları", ext: "xlsx" },
  { key: "denizbank", label: "Denizbank TÖS", title: "Denizbank TL Toplu Ödeme (TÖS) şablonu (kayıtlı format) — görünen gider faturaları", ext: "xlsm" },
];

// blob yanıtlı isteklerde hata gövdesi Blob gelir → JSON detail okunur
async function blobError(e) {
  const d = e?.response?.data;
  if (d instanceof Blob) {
    try { const j = JSON.parse(await d.text()); if (j?.detail) return toast.error(typeof j.detail === "string" ? j.detail : j.detail.message || "İşlem başarısız"); } catch { /* düş */ }
  }
  toastApiError(e);
}

function BucketTable({ items, testid, onChanged, focusId, part, bucket }) {
  const navigate = useNavigate();
  const [hf, setHf, visRows, autoOpts] = useColFilters(items, PP_COLS);
  const [busy, setBusy] = useState("");
  const [missing, setMissing] = useState(null);   // { rows:[{vendor, cari_id, count, total}], with_iban, total_invoices }
  const ids = () => visRows.map((r) => r.id);
  const invoiceIds = () => visRows.filter((r) => (r.kind || "invoice") === "invoice" && !r.paid).map((r) => r.id);
  const exportXlsx = async () => {
    setBusy("xlsx");
    try {
      await postDownload("/invoices/payment-plan/export", { part, bucket, ids: ids() }, `odeme-plani-${part === "expense" ? "gider" : "gelir"}-${bucket}.xlsx`);
      toast.success(`${visRows.length} satır Excel'e aktarıldı — Firma IBAN'ı cari kartından alındı`);
    } catch (e) { await blobError(e); } finally { setBusy(""); }
  };
  const loadMissing = async (silent) => {
    setBusy("iban");
    try {
      const { data } = await http.post("/invoices/payment-plan/missing-iban", { bucket, ids: ids() });
      setMissing(data);
      if (!silent) (data.rows.length ? toast.warning : toast.success)(data.rows.length ? `${data.rows.length} firmanın cari kartında IBAN yok` : "Görünen tüm faturaların firma IBAN'ı tanımlı");
    } catch (e) { toastApiError(e); } finally { setBusy(""); }
  };
  const bulkPay = async (n) => {
    if (!window.confirm(`Banka dosyası indirildi. Dosyaya yazılan ${n} fatura tek adımda "Ödendi" olarak işaretlensin mi?`)) return;
    try {
      const { data } = await http.post("/invoices/bulk-pay", { ids: invoiceIds() });
      toast.success(`${data.paid} fatura Ödendi olarak işaretlendi${data.skipped.length ? ` · ${data.skipped.length} satır atlandı` : ""}`);
      onChanged?.();
    } catch (e) { toastApiError(e); }
  };
  const bankFile = async (bf) => {
    setBusy(bf.key);
    try {
      const h = await postDownload("/invoices/payment-plan/bank-file", { bank: bf.key, bucket, ids: ids() }, `${bf.key}-toplu-odeme-${new Date().toISOString().slice(0, 10)}.${bf.ext}`);
      const n = Number(h["x-rows"] || 0), miss = Number(h["x-missing-iban"] || 0), skip = Number(h["x-skipped"] || 0);
      const parts = [n ? `${n} fatura yazıldı` : "Banka dosyası indirildi"];
      if (miss) parts.push(`${miss} firmanın cari kartında IBAN yok — dosyada boş bırakıldı`);
      if (skip) parts.push(`${skip} çek / kart / kredi taksidi dosyaya alınmadı`);
      (miss ? toast.warning : toast.success)(parts.join(" · "));
      if (miss) await loadMissing(true);
      setBusy("");
      if (n) await bulkPay(n);
    } catch (e) { await blobError(e); } finally { setBusy(""); }
  };
  const goCari = (m) => {
    const from = window.location.pathname + window.location.search;
    navigate(m.cari_id ? `/cariler?cari=${m.cari_id}&duzenle=1` : `/cariler?yeni=1&ad=${encodeURIComponent(m.vendor)}`, { state: { from } });
  };
  const togglePaid = async (inv) => {
    try {
      if (inv.kind === "credit_card") {
        if (!inv.registered_card_id) return toast.error("Kayıtlı kart bilgisi yok — işlemleri Veri Girişi'nden tek tek işaretleyin");
        const { data } = await http.patch("/credit-cards/period/pay", { registered_card_id: inv.registered_card_id, period_end: inv.period_end, paid: !inv.paid });
        toast.success(inv.paid ? `Ekstre ödemesi geri alındı (${data.count} işlem)` : `Ekstre ödendi — ${data.count} işlem kapatıldı`);
        onChanged?.();
        return;
      }
      if (inv.kind === "loan") {
        await http.patch(`/bank-loans/${inv.loan_id}/installments/${inv.installment_no}/${inv.paid ? "unpay" : "pay"}`);
        toast.success(inv.paid ? `${inv.installment_no}. taksit geri alındı` : `${inv.installment_no}. taksit ödendi`);
        onChanged?.();
        return;
      }
      const base = inv.kind === "check" ? "/checks" : "/invoices";
      await http.patch(`${base}/${inv.id}/${inv.paid ? "unpay" : "pay"}`);
      toast.success(inv.paid ? "Ödeme geri alındı" : "Ödendi olarak işaretlendi");
      onChanged?.();
    } catch (e) { toastApiError(e); }
  };
  return (
    <div className="bg-white" data-testid={`${testid}-wrap`}>
      <div className="flex items-stretch border-b-2 border-amber-300 bg-amber-50/60">
        <div className="flex-1 [&>div]:border-b-0"><FilterTotal rows={visRows} sums={SUM_TOTAL} testid={`${testid}-filter-total`} /></div>
        <button type="button" onClick={exportXlsx} disabled={!!busy || visRows.length === 0} data-testid={`${testid}-export-xlsx`}
          title="Görünen satırları Excel olarak indir — Firma IBAN sütunu Cari Listesi'ndeki firma kartından alınır"
          className="my-1 mr-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-800 text-[11px] font-semibold hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          <FileSpreadsheet className="w-3.5 h-3.5" /> {busy === "xlsx" ? "Hazırlanıyor…" : "Excel İndir (IBAN'lı)"}
        </button>
        {part === "expense" && BANK_FILES.map((bf) => (
          <button key={bf.key} type="button" onClick={() => bankFile(bf)} disabled={!!busy || visRows.length === 0} data-testid={`${testid}-bank-${bf.key}`} title={bf.title}
            className="my-1 mr-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-sky-300 bg-sky-50 text-sky-900 text-[11px] font-semibold hover:bg-sky-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Landmark className="w-3.5 h-3.5" /> {busy === bf.key ? "Hazırlanıyor…" : bf.label}
          </button>
        ))}
        {part === "expense" && (
          <button type="button" onClick={() => (missing ? setMissing(null) : loadMissing(false))} disabled={!!busy || visRows.length === 0} data-testid={`${testid}-missing-iban`}
            title="Görünen gider faturalarında cari kartı IBAN'ı boş firmaları listele — tek tıkla cari kartını düzenle"
            className={`my-1 mr-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[11px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${missing ? "border-orange-400 bg-orange-100 text-orange-900" : "border-orange-300 bg-orange-50 text-orange-900 hover:bg-orange-100"}`}>
            <AlertTriangle className="w-3.5 h-3.5" /> {busy === "iban" ? "Kontrol…" : missing ? "IBAN Listesini Kapat" : "IBAN Eksikleri"}
          </button>
        )}
      </div>
      {missing && (
        <div className="px-4 py-2.5 border-b border-orange-200 bg-orange-50/70 text-xs" data-testid={`${testid}-missing-iban-panel`}>
          {missing.rows.length === 0 ? (
            <span className="font-semibold text-emerald-800">Görünen {missing.total_invoices} faturanın tamamında firma IBAN'ı tanımlı.</span>
          ) : (
            <>
              <div className="font-semibold text-orange-900 mb-1.5">Cari kartında IBAN olmayan firmalar ({missing.rows.length}) — banka dosyasında boş kalır; kartı düzenleyip IBAN girin (ESC ile buraya dönülür):</div>
              <ul className="flex flex-wrap gap-2">
                {missing.rows.map((m) => (
                  <li key={m.vendor} className="inline-flex items-center gap-2 pl-2.5 pr-1 py-1 rounded-md border border-orange-200 bg-white" data-testid={`${testid}-missing-iban-row`}>
                    <span className="font-semibold text-slate-800">{m.vendor}</span>
                    <span className="text-slate-500 font-mono">{m.count} fatura · {fmtTL(m.total)}</span>
                    <button type="button" onClick={() => goCari(m)} data-testid={`${testid}-missing-iban-fix`}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-white text-[10px] font-bold uppercase ${m.cari_id ? "bg-orange-600 hover:bg-orange-700" : "bg-slate-700 hover:bg-slate-900"}`}>
                      <ExternalLink className="w-3 h-3" /> {m.cari_id ? "IBAN Gir" : "Cari Kart Aç"}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
      <div className="scroll-x" data-testid={`${testid}-scroll`}>
      <table className="w-full min-w-[900px] tbl" data-testid={testid}>
        <thead className="bg-slate-50">
          <tr>
            {PP_COLS.map((c, i) => (
              <th key={c.key} className={`px-3 py-2 text-xs uppercase tracking-wider text-slate-600 font-semibold ${c.key === "total_amount" ? "text-right" : "text-left"}`}>
                <FilterTh col={c} hf={hf} setHf={setHf} testid={`${testid}-hf-${c.key}`} align={i >= PP_COLS.length - 2 ? "right" : "left"} autoOptions={autoOpts[c.key]} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visRows.map((inv) => (
            <tr key={inv.id} data-pp-id={inv.id} className={`${inv.kind === "check" ? "border-t border-amber-200 bg-amber-50" : "border-t border-slate-100"} ${inv.id === focusId ? "!bg-yellow-200 ring-2 ring-inset ring-yellow-500" : ""}`} data-testid={inv.kind === "check" ? `payment-check-row-${inv.id}` : `payment-invoice-row-${inv.id}`}>
              <td className="px-3 py-2 text-xs">
                {inv.vendor}
                {inv.kind === "check" && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-400 text-amber-900 text-[9px] font-bold uppercase tracking-wider" data-testid={`check-badge-${inv.id}`}>Çek</span>}
                {inv.kind === "credit_card" && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-sky-500 text-white text-[9px] font-bold uppercase tracking-wider" data-testid={`cc-badge-${inv.id}`}>Kart</span>}
                {inv.kind === "loan" && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-violet-600 text-white text-[9px] font-bold uppercase tracking-wider" data-testid={`loan-badge-${inv.id}`}>Kredi Taksidi</span>}
              </td>
              <td className="px-3 py-2 text-xs">
                <button data-testid={`pp-goto-${inv.id}`} title="Kayda git (ESC ile Ödeme Planı'na geri dönülür)"
                  onClick={() => {
                    const don = encodeURIComponent(window.location.pathname + window.location.search);
                    if (inv.kind === "check") navigate(`/veri-girisi?bolum=cek&kayit=${inv.id}&don=${don}`);
                    else if (inv.kind === "credit_card") navigate(`/veri-girisi?bolum=kredi&alt=kart&don=${don}`);
                    else if (inv.kind === "loan") navigate(`/veri-girisi?bolum=kredi&don=${don}`);
                    else navigate(`/veri-girisi?bolum=fatura&fatura=${inv.id}&don=${don}`);
                  }}
                  className="inline-flex items-center gap-1 font-mono text-amber-800 hover:text-amber-950 hover:underline underline-offset-2 transition-colors">
                  {inv.invoice_no || "Kayda git"} <ExternalLink className="w-3 h-3 opacity-60" />
                </button>
              </td>
              <td className="px-3 py-2 text-xs">{fmtDate(inv.due_date)}</td>
              <td className="px-3 py-2 text-xs">{inv.kind === "check" || inv.kind === "loan" ? inv.bank : inv.category || "—"}</td>
              <td className="px-3 py-2 text-xs text-slate-600 max-w-[160px] 2xl:max-w-[260px] truncate" title={inv.description || ""}>{inv.description || "—"}</td>
              <td className="numeric-cell px-3 py-2 text-xs font-semibold">
                {inv.advance_deduction || inv.quarter_advance_deduction ? (
                  <div title="Avans mahsubu düşülmüş ödenecek tutar (eksiye düşmez; yetmeyen taksit kesintisi sonraki faturaya devreder)" data-testid={`pp-net-${inv.id}`} className="space-y-0.5 py-0.5">
                    <div className="text-slate-700 whitespace-nowrap">{fmtTL(inv.gross_amount)} <span className="text-[9px] font-normal text-slate-400 uppercase">Fatura</span></div>
                    {inv.quarter_advance_deduction ? <div className="text-[11px] font-normal text-orange-700 whitespace-nowrap">− {fmtTL(inv.quarter_advance_deduction)} <span className="text-[9px] uppercase">1/4 Avans</span></div> : null}
                    {inv.advance_deduction ? <div className="text-[11px] font-normal text-amber-700 whitespace-nowrap">− {fmtTL(inv.advance_deduction)} <span className="text-[9px] uppercase">Taksitli Avans</span></div> : null}
                    <div className="text-emerald-800 border-t border-slate-200 pt-0.5 whitespace-nowrap">= {fmtTL(inv.total_amount)}</div>
                  </div>
                ) : fmtTL(inv.total_amount)}
              </td>
              <td className="px-3 py-2 text-xs whitespace-nowrap">
                <button type="button" onClick={() => togglePaid(inv)} data-testid={`pp-toggle-paid-${inv.id}`} title={inv.kind === "credit_card" ? (inv.paid ? "Tıkla: ekstre dönemindeki tüm işlemleri Ödenmedi yap" : "Tıkla: ekstre dönemindeki tüm işlemleri Ödendi yap") : (inv.paid ? "Tıkla: Ödenmedi yap" : "Tıkla: Ödendi yap")}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider border transition-colors ${inv.paid ? "bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-amber-100 hover:text-amber-800 hover:border-amber-300" : "bg-amber-100 text-amber-800 border-amber-300 hover:bg-emerald-100 hover:text-emerald-800 hover:border-emerald-300"}`}>
                  {inv.paid ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />} {inv.paid ? "Ödendi" : "Ödenmedi"}
                </button>
              </td>
            </tr>
          ))}
          {visRows.length === 0 && <tr><td colSpan={PP_COLS.length} className="py-6 text-center text-sm text-slate-500" data-testid={items.length === 0 ? `bucket-empty-${part}-${bucket}` : `${testid}-filter-empty`}>{items.length === 0 ? "Bu başlıkta kayıt yok — Excel / banka dosyası düğmeleri kayıt olduğunda etkinleşir" : "Filtrelerle eşleşen kayıt yok"}</td></tr>}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function UninvoicedTable({ rows }) {
  const [hf, setHf, visRows, autoOpts] = useColFilters(rows, UNINV_COLS);
  return (
    <div className="bg-white">
      <FilterTotal rows={visRows} sums={SUM_ESTIMATED} testid="uninvoiced-filter-total" />
      <div className="scroll-x">
      <table className="w-full min-w-[760px] tbl">
        <thead className="bg-slate-50">
          <tr>
            {UNINV_COLS.map((c, i) => (
              <th key={c.key} className={`px-3 py-2 text-xs uppercase tracking-wider text-slate-600 font-semibold ${i === UNINV_COLS.length - 1 ? "text-right" : "text-left"}`}>
                <FilterTh col={c} hf={hf} setHf={setHf} testid={`uninvoiced-hf-${c.key}`} align={i === UNINV_COLS.length - 1 ? "right" : "left"} autoOptions={autoOpts[c.key]} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visRows.map((u, i) => (
            <tr key={`${u.project_id}-${u.category_name}-${u.period}`} className="border-t border-slate-100" data-testid={`uninvoiced-row-${i}`}>
              <td className="px-3 py-2 text-xs font-semibold text-slate-900">{u.project_name}</td>
              <td className="px-3 py-2 text-xs">{u.category_name}</td>
              <td className="px-3 py-2 text-xs">{u.period_label}</td>
              <td className="px-3 py-2 text-xs text-red-700 font-semibold">{fmtDate(u.deadline)} geçti</td>
              <td className="numeric-cell px-3 py-2 text-xs font-semibold text-red-700" data-testid={`uninvoiced-amount-${i}`} title="Mutabakat sağlanması halinde tahminen bu tutarda hakediş faturası kesilmemiştir">{fmtTL(u.estimated_total)}</td>
            </tr>
          ))}
          {visRows.length === 0 && <tr><td colSpan={UNINV_COLS.length} className="py-6 text-center text-sm text-slate-500" data-testid="uninvoiced-filter-empty">Filtrelerle eşleşen kayıt yok</td></tr>}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function DraftsTable({ drafts }) {
  const [hf, setHf, visRows, autoOpts] = useColFilters(drafts, DRAFT_COLS);
  return (
    <div className="bg-white">
      <FilterTotal rows={visRows} sums={SUM_TOTAL} testid="drafts-filter-total" />
      <div className="scroll-x">
      <table className="w-full min-w-[900px] tbl">
        <thead className="bg-slate-50">
          <tr>
            {DRAFT_COLS.map((c, i) => (
              <th key={c.key} className={`px-3 py-2 text-xs uppercase tracking-wider text-slate-600 font-semibold ${i === DRAFT_COLS.length - 1 ? "text-right" : "text-left"}`}>
                <FilterTh col={c} hf={hf} setHf={setHf} testid={`draft-hf-${c.key}`} align={i === DRAFT_COLS.length - 1 ? "right" : "left"} autoOptions={autoOpts[c.key]} />
              </th>
            ))}
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {visRows.map((inv) => (
            <tr key={inv.id} className="border-t border-slate-100" data-testid={`draft-invoice-row-${inv.id}`}>
              <td className="px-3 py-2 text-xs">{inv.vendor}</td>
              <td className="px-3 py-2 text-xs">{inv.invoice_no || "—"}</td>
              <td className="px-3 py-2 text-xs">{fmtDate(inv.invoice_date)}</td>
              <td className="px-3 py-2 text-xs">{fmtDate(inv.due_date)}</td>
              <td className="px-3 py-2 text-xs">{inv.project_name || "—"}</td>
              <td className="px-3 py-2 text-xs">{inv.direction === "income" ? "Gelir" : "Gider"}</td>
              <td className="numeric-cell px-3 py-2 text-xs font-semibold">{fmtTL(inv.total_amount)}</td>
              <td className="px-3 py-2 text-xs text-right">
                <Link to={`/veri-girisi?bolum=fatura&fatura=${inv.id}&don=/odeme-plani`} data-testid={`draft-goto-${inv.id}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-600 text-white text-[10px] font-bold uppercase hover:bg-amber-700 transition-colors">
                  <ExternalLink className="w-3 h-3" /> Faturaya Git
                </Link>
              </td>
            </tr>
          ))}
          {visRows.length === 0 && <tr><td colSpan={DRAFT_COLS.length + 1} className="py-6 text-center text-sm text-slate-500" data-testid="drafts-filter-empty">Filtrelerle eşleşen kayıt yok</td></tr>}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function Section({ title, note, icon: Icon, part, testidPrefix, onChanged, focusId }) {
  const [openBuckets, setOpenBuckets] = useState({});
  const focusBucket = focusId ? bucketMeta.find((b) => (part.buckets[b.key] || []).some((x) => x.id === focusId))?.key : null;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !e.target.closest("input,textarea,select"))
        setOpenBuckets(Object.fromEntries(bucketMeta.map((b) => [b.key, false])));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <div className="space-y-4" data-testid={`section-${testidPrefix}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5 text-slate-700" />
        <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
        {note && <span className="text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-0.5" data-testid={`note-${testidPrefix}`}>{note}</span>}
        <span className="ml-auto text-sm font-mono font-bold text-slate-900">{fmtTL(part.unpaid_total)}</span>
      </div>

      <div className="space-y-3">
        {bucketMeta.map((b) => {
          const items = part.buckets[b.key] || [];
          const total = part.totals[b.key] || 0;
          const BIcon = b.icon;
          const isOpen = openBuckets[b.key] ?? (b.key === focusBucket || b.key === "overdue" || b.key === "today" || b.key === "paid_today");
          return (
            <div key={b.key} className={`rounded-lg border overflow-hidden ${colorClasses[b.color]}`} data-testid={`bucket-${testidPrefix}-${b.key}`}>
              <button type="button" onClick={() => setOpenBuckets((s) => ({ ...s, [b.key]: !isOpen }))} data-testid={`bucket-toggle-${testidPrefix}-${b.key}`}
                className="w-full p-4 flex items-center gap-3 text-left transition-colors hover:brightness-[0.97]">
                <BIcon className="w-4 h-4 shrink-0" />
                <span className="text-[10px] uppercase tracking-widest font-semibold">{b.label}</span>
                <span className="text-[11px]">{items.length} fatura</span>
                <span className="ml-auto text-xl font-bold font-mono">{fmtTL(total)}</span>
                {isOpen ? <ChevronDown className="w-4 h-4 opacity-60" /> : <ChevronRight className="w-4 h-4 opacity-60" />}
              </button>
              {isOpen && (
                <BucketTable items={items} testid={`bucket-table-${testidPrefix}-${b.key}`} onChanged={onChanged} focusId={focusId} part={testidPrefix} bucket={b.key} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
